import produce from "immer"
import { ASC, IDLE } from "const"
import {
    Lifecycle,
    LifecyclePhase,
    LifecycleConnection,
} from "server/models"
import { InferValueTypes } from "client/utils"
import * as types from "./types"
import * as actions from "./actions"

type ActionTypes = ReturnType<InferValueTypes<typeof actions>>

export const initLifecycle: Lifecycle = {
    id: null,
    originalLifecycleId: null,
    accountId: null,
    projectId: null,
    name: "",
    description: "",
    type: "waterfall",
    ownerAccount: null,
    creator: null,
    responsible: null,
    order: 0,
    default: false,
}

export const initLifecyclePhase: LifecyclePhase = {
    id: null,
    lifecycleId: null,
    phaseId: null,
    position: null,
    name: "",
    objective: "",
    creator: null,
    responsible: null,
}

export const initLifecycleConnection: LifecycleConnection = {
    id: null,
    lifecycleId: null,
    phaseId: null,
    startPhaseId: null,
    endPhaseId: null,
    startPhaseSide: null,
    endPhaseSide: null,
    points: null,
    pointsArray: [],
    doubleEnd: false,
    orthogonal: true,
}


const initLifecycleState: types.LifecycleState = {
    lifecyclePath: [],
    baseRoute: null,
    onSelectPhase: null,

    lifecycleList: {},
    lifecycleIds: [],
    lifecycleView: initLifecycle,
    lifecycleEdit: initLifecycle,

    reloadList: true,
    reloadView: true,
    reloadEdit: true,

    // lifecycle model
    model: {
        phaseList: [],
        activePhase: null,
        confirmDeletePhase: null,
        reloadActivePhase: false,

        connectionList: [],
        activeConnection: null,
        confirmDeleteConnection: null,
        reloadActiveConnection: false,

        dragPhase: null,
        drawConnection: null,
        loadState: IDLE,
        reloadModel: true,
    },

    // import
    importFromLibrary: {
        lifecycleList: {},
        lifecycleIds: [],
        sortColumn: "name",
        sortOrder: ASC,
        filters: {
            search: null,
        },
    },

    importFromAccount: {
        lifecycleList: {},
        lifecycleIds: [],
        sortColumn: "name",
        sortOrder: ASC,
        filters: {
            search: null,
        },
    },

    importLifecycleId: null,
    reloadImported: true,
}


const lifecycleReducer = produce((
    draft: types.LifecycleState = initLifecycleState,
    action: ActionTypes = null,
): types.LifecycleState => {
    switch (action.type) {
        case types.LIFECYCLE_PATH:
            draft.lifecyclePath = action.path
            return draft

        case types.LIFECYCLE_BASE_ROUTE:
            draft.baseRoute = action.baseRoute
            return draft

        case types.LIFECYCLE_ON_SELECT_PHASE:
            draft.onSelectPhase = action.onSelect
            return draft

        case types.LIFECYCLE_LIST:
            draft.lifecycleList = action.lifecycleList
            return draft

        case types.LIFECYCLE_IDS:
            draft.lifecycleIds = action.lifecycleIds
            return draft

        case types.LIFECYCLE_VIEW:
            draft.lifecycleView = action.lifecycle
            return draft

        case types.LIFECYCLE_EDIT:
            draft.lifecycleEdit = action.lifecycle
            return draft

        case types.LIFECYCLE_RELOAD_LIST:
            draft.reloadList = action.value
            return draft

        case types.LIFECYCLE_RELOAD_VIEW:
            draft.reloadView = action.value
            return draft

        case types.LIFECYCLE_RELOAD_EDIT:
            draft.reloadEdit = action.value
            return draft


        // lifecycle model
        case types.LIFECYCLE_PHASE_LIST:
            draft.model.phaseList = action.phaseList
            return draft

        case types.LIFECYCLE_PHASE_ACTIVE:
            draft.model.activePhase = action.phase
            return draft

        case types.LIFECYCLE_PHASE_CONFIRM_DELETE:
            draft.model.confirmDeletePhase = action.phase
            return draft

        case types.LIFECYCLE_PHASE_RELOAD_ACTIVE:
            draft.model.reloadActivePhase = action.value
            return draft

        case types.LIFECYCLE_CONNECTION_LIST:
            draft.model.connectionList = action.connectionList
            return draft

        case types.LIFECYCLE_CONNECTION_ACTIVE:
            draft.model.activeConnection = action.connection
            return draft

        case types.LIFECYCLE_CONNECTION_CONFIRM_DELETE:
            draft.model.confirmDeleteConnection = action.connection
            return draft

        case types.LIFECYCLE_CONNECTION_RELOAD_ACTIVE:
            draft.model.reloadActiveConnection = action.value
            return draft

        case types.LIFECYCLE_MODEL_DRAG_PHASE:
            draft.model.dragPhase = action.dragPhase
            return draft

        case types.LIFECYCLE_MODEL_DRAW_CONNECTION:
            draft.model.drawConnection = action.drawConnection
            return draft

        case types.LIFECYCLE_MODEL_LOAD_STATE:
            draft.model.loadState = action.value
            return draft

        case types.LIFECYCLE_MODEL_RELOAD:
            draft.model.reloadModel = action.value
            return draft


        // import from library
        case types.LIFECYCLE_IMPORT_FROM_LIBRARY_LIST:
            draft.importFromLibrary.lifecycleList = action.lifecycleList
            return draft

        case types.LIFECYCLE_IMPORT_FROM_LIBRARY_IDS:
            draft.importFromLibrary.lifecycleIds = action.lifecycleIds
            return draft

        case types.LIFECYCLE_IMPORT_FROM_LIBRARY_SORT:
            draft.importFromLibrary.sortColumn = action.sortColumn
            draft.importFromLibrary.sortOrder = action.sortOrder
            return draft

        case types.LIFECYCLE_IMPORT_FROM_LIBRARY_FILTERS:
            draft.importFromLibrary.filters = action.filters
            return draft


        // import from account
        case types.LIFECYCLE_IMPORT_FROM_ACCOUNT_LIST:
            draft.importFromAccount.lifecycleList = action.lifecycleList
            return draft

        case types.LIFECYCLE_IMPORT_FROM_ACCOUNT_IDS:
            draft.importFromAccount.lifecycleIds = action.lifecycleIds
            return draft

        case types.LIFECYCLE_IMPORT_FROM_ACCOUNT_SORT:
            draft.importFromAccount.sortColumn = action.sortColumn
            draft.importFromAccount.sortOrder = action.sortOrder
            return draft

        case types.LIFECYCLE_IMPORT_FROM_ACCOUNT_FILTERS:
            draft.importFromAccount.filters = action.filters
            return draft


        case types.LIFECYCLE_IMPORT_ID:
            draft.importLifecycleId = action.lifecycleId
            return draft

        case types.LIFECYCLE_RELOAD_IMPORTED:
            draft.reloadImported = action.value
            return draft

        default:
            return draft
    }
})
export default lifecycleReducer
